import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=unknown, false=guest, object=user
  const [loading, setLoading] = useState(true);

  // Only a real auth answer (401/403) means guest; a restarting backend (network error / 5xx) keeps the stored session and retries.
  const refresh = useCallback(async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        setLoading(false);
        return data.user;
      } catch (e) {
        const st = e.response?.status;
        if (st === 401 || st === 403 || attempt >= 40) {
          setUser(false);
          setLoading(false);
          return null;
        }
        await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** attempt, 5000)));
      }
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    // Reset session-scoped UI toggles (Admin Mode, etc.) so a fresh
    // login always starts with a clean slate — even for the same user.
    try { sessionStorage.removeItem("st.adminMode.on"); } catch { /* noop */ }
    setUser(false);
  };

  // Adopt the user returned by POST /auth/login so sign-in never waits on a second round-trip.
  const signedIn = useCallback((u) => { setUser(u); setLoading(false); }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, signedIn }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
